import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { PencilSimpleIcon } from '@phosphor-icons/react';
import { Input } from 'src/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { cn } from '@/lib/utils';
import { overrideMetaLine } from '../../../replenishment/components/overrideRows';
import {
  formatInlineValue,
  type InlineOverrideField,
} from './inlineOverrideField';
import { OverrideActions } from './OverrideActions';
import { OVERRIDDEN_VALUE_CLASS } from './overrideMarking';
import { useInlineOverrideDraft } from './useInlineOverrideDraft';

/**
 * A card value that can be overridden: it looks like one, and clicking it opens
 * a small editor in place — the value and an optional comment — that writes
 * through the same save the full override editors use.
 *
 * An active override is blue and says who set it; a field that cannot be pinned
 * right now (or a viewer without update permission) keeps the marking but does
 * not open.
 */
export function OverridableValue({
  field,
  canEdit,
  children,
}: {
  field: InlineOverrideField;
  canEdit: boolean;
  children: ReactNode;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const { override } = field;
  const editable = canEdit && field.lockedReason === null;
  const marking = cn(override && OVERRIDDEN_VALUE_CLASS);
  const summary = override
    ? gt('Override {value}. {meta}', {
        context: 'forecast detail overridable value tooltip',
        value: formatInlineValue(override.value, field.unit),
        meta: overrideMetaLine(override, gt, field.grain),
      })
    : (field.lockedReason ??
      gt('Click to set an override', {
        context: 'forecast detail overridable value tooltip, none set',
      }));

  if (!editable) {
    return (
      <span className={marking} title={summary}>
        {children}
      </span>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={summary}
          className={cn(
            'group inline-flex items-center gap-1 rounded-sm underline decoration-dotted underline-offset-2 hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring',
            marking,
          )}
        >
          <PencilSimpleIcon className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <OverrideForm field={field} onDone={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

// Mounted only while the popover is open, so each opening starts from the
// governing override.
function OverrideForm({
  field,
  onDone,
}: {
  field: InlineOverrideField;
  onDone: () => void;
}) {
  const gt = useGT();
  const id = useId();
  const { override } = field;
  const { raw, setRaw, problem, canSave, windowState, save } =
    useInlineOverrideDraft(field, onDone);

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      void save();
    }
  }

  return (
    <div
      className="space-y-2 text-xs"
      onKeyDown={onKeyDown}
      role="group"
      aria-label={gt('Edit {field} override', {
        context: 'inline override editor label',
        field: field.label,
      })}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{field.label}</span>
        <span className="text-muted-foreground">
          {gt('Calculated {value}', {
            context: 'forecast detail override popover calculated value',
            value: formatInlineValue(field.calculated, field.unit),
          })}
        </span>
      </div>
      <div>
        <label htmlFor={`${id}-value`} className="text-muted-foreground">
          {gt('Override value', {
            context: 'forecast detail override popover value field',
          })}
        </label>
        <Input
          id={`${id}-value`}
          autoFocus
          type="number"
          inputMode="decimal"
          step={field.unit === 'days' ? 1 : 'any'}
          value={raw}
          placeholder={formatInlineValue(field.effective, field.unit)}
          onChange={(e) => setRaw(e.target.value)}
          aria-invalid={problem !== null || undefined}
          aria-describedby={problem ? `${id}-problem` : undefined}
        />
        {problem ? (
          <p id={`${id}-problem`} className="mt-0.5 text-destructive">
            {problem}
          </p>
        ) : null}
      </div>
      <div>
        <label htmlFor={`${id}-comment`} className="text-muted-foreground">
          {gt('Comment (optional)', {
            context: 'forecast detail override popover comment field',
          })}
        </label>
        <Input
          id={`${id}-comment`}
          value={windowState.reason}
          onChange={(e) => windowState.setReason(e.target.value)}
        />
      </div>
      <p className="text-muted-foreground">
        {override && field.replaces
          ? gt(
              'Replaces the current override and keeps its dates. Set start, end and review dates under Current Planning Overrides.',
              { context: 'forecast detail override popover replace notice' },
            )
          : gt(
              'Applies from now with no end date. Set start, end and review dates under Current Planning Overrides.',
              { context: 'forecast detail override popover new notice' },
            )}
      </p>
      <div className="flex justify-end gap-2">
        <OverrideActions
          canSave={canSave}
          onCancel={onDone}
          onSave={() => void save()}
        />
      </div>
    </div>
  );
}
